import React, { useEffect, useState } from 'react';
import { Button } from '../../components/UI/Button';
import { useToast } from '../../components/UI/ToastProvider';
import { AGENT_PLATFORM_FEATURE } from '../../constants/agentPlatform';
import { agentPlatformService } from '../../services/agentPlatformService';
import type { CustomerAgentPlatformConfig } from '../../types/agentPlatform';

const FEATURE_LABELS: Record<string, string> = {
  [AGENT_PLATFORM_FEATURE.platform]: 'Plataforma Agêntica',
  [AGENT_PLATFORM_FEATURE.agents]: 'Assistente de IA',
  [AGENT_PLATFORM_FEATURE.tools]: 'Tools',
  [AGENT_PLATFORM_FEATURE.flows]: 'Fluxos / Composer',
};

interface Props {
  customerId: string;
  customerIsOwner?: boolean;
}

export const CustomerAgentPlatformPanel: React.FC<Props> = ({
  customerId,
  customerIsOwner = false,
}) => {
  const { addToast } = useToast();
  const [config, setConfig] = useState<CustomerAgentPlatformConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    agentPlatformService
      .getCustomerAgentPlatformConfig(customerId)
      .then(setConfig)
      .catch(() => addToast('Erro ao carregar config agêntica.', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [customerId]);

  const saveMonitoring = async (enabled: boolean | null) => {
    setSaving(true);
    try {
      const updated = await agentPlatformService.updateCustomerAgentPlatformConfig(customerId, {
        agentMonitoringEnabled: enabled,
      });
      setConfig(updated);
      addToast('Configuração agêntica salva.', 'success');
    } catch {
      addToast('Erro ao salvar configuração.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (customerIsOwner) {
    return (
      <section className="mt-8 border rounded-lg p-6 bg-slate-50">
        <h2 className="text-lg font-semibold mb-2">Plataforma Agêntica</h2>
        <p className="text-sm text-gray-600">
          Tenant owner (Baruk): licenciamento agêntico é o catálogo completo. Use a página de
          Monitoria para políticas globais.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-8 border rounded-lg p-6 bg-white shadow-sm">
      <h2 className="text-lg font-semibold mb-1">Plataforma Agêntica</h2>
      <p className="text-sm text-gray-600 mb-4">
        Licenciamento e override de monitoria para este cliente.
      </p>

      {loading && <p className="text-sm text-gray-500">Carregando…</p>}

      {config && (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Features licenciadas</h3>
            {config.licensedAgentFeatures.length === 0 ? (
              <p className="text-sm text-amber-700">
                Nenhuma feature agêntica no plano ativo. Ajuste a assinatura acima.
              </p>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {config.licensedAgentFeatures.map(code => (
                  <li
                    key={code}
                    className="text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-800"
                  >
                    {FEATURE_LABELS[code] ?? code}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Monitoria de execuções</h3>
            <dl className="text-sm grid sm:grid-cols-3 gap-3 mb-4">
              <div>
                <dt className="text-gray-500">Global</dt>
                <dd className="font-medium">{config.globalMonitoringEnabled ? 'Ativa' : 'Inativa'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Override cliente</dt>
                <dd className="font-medium">
                  {config.agentMonitoringEnabled == null
                    ? '—'
                    : config.agentMonitoringEnabled
                      ? 'Forçada ON'
                      : 'Forçada OFF'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Efetivo</dt>
                <dd
                  className={
                    config.effectiveMonitoringEnabled
                      ? 'font-medium text-emerald-700'
                      : 'font-medium text-gray-500'
                  }
                >
                  {config.effectiveMonitoringEnabled ? 'Coleta ativa' : 'Coleta inativa'}
                </dd>
              </div>
            </dl>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={saving} onClick={() => saveMonitoring(true)}>
                Ativar override
              </Button>
              <Button size="sm" variant="secondary" disabled={saving} onClick={() => saveMonitoring(false)}>
                Desativar override
              </Button>
              <Button size="sm" variant="ghost" disabled={saving} onClick={() => saveMonitoring(null)}>
                Limpar override
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
